import { pojos, PojosMetadataMap } from '@automapper/pojos';
import { createMapper, createMap, forMember, mapFrom } from '@automapper/core';

import { IBeneficiaryTableWizardSource, IBeneficiaryTableWizardDTO } from '../entities/IBeneficiaryTableWizard';
import { IInquiryTableWizardDTO, IInquiryTableWizardSource, IWizardStakeProfile, IWizardReseller, IWizardProductInformationItem } from '../entities/IInquiryTableWizard';
import { wizardStakeProfileToInquiryPartiesWire } from './wizardPrincipalProfile';

const mapper = createMapper({
    strategyInitializer: pojos()
});

// Beneficiary Table Wizard
PojosMetadataMap.create<IBeneficiaryTableWizardSource>('BeneficiaryTableWizardSource', {
    formData: Object,
    step: Number,
    amountValue: String,
    readPolicy: Boolean,
    contextLength: Boolean,
    tableLoaded: Boolean
});

PojosMetadataMap.create<IBeneficiaryTableWizardDTO>('BeneficiaryTableWizardDTO', {
    declaration: Object,
    thirdParties: Array,
    information: Object,
    message: Object,
    declarationType: String
});

createMap<IBeneficiaryTableWizardSource, IBeneficiaryTableWizardDTO>(
    mapper,
    'BeneficiaryTableWizardSource',
    'BeneficiaryTableWizardDTO',
    forMember(
        d => d.declaration,
        mapFrom(s => s.formData.declaration)
    ),
    forMember(
        d => (d as any).priceInformation,
        mapFrom(s => {
            const src = (s as any)?.formData?.priceCalculationCriteria || {};
            return {
                conversionRate: src?.conversionRate,
                currency: ensureReferentialKey(src?.currency),
                priceCategory: ensureReferentialKey(src?.priceCategory),
                stockCategory: ensureReferentialKey(src?.stockCategory)
            };
        })
    ),
    forMember(
        d => d.thirdParties,
        mapFrom(s => s.formData.thirdParties)
    ),
    forMember(
        d => d.information,
        mapFrom(s => {
            // Remove specific fields from information as per business logic
            const { firstName, lastName, picture, ...rest } = s.formData.information;
            return rest;
        })
    ),
    forMember(
        d => d.message,
        mapFrom(s => s.formData.message)
    ),
    forMember(
        d => d.declarationType,
        mapFrom(s => s.formData.declarationType)
    ),
    forMember(
        d => d.clusterIdToUpdate,
        mapFrom(s => s.formData.clusterIdToUpdate)
    )
);

export const mapBeneficiaryTableWizard = (source: IBeneficiaryTableWizardSource): IBeneficiaryTableWizardDTO => {
    return mapper.map<IBeneficiaryTableWizardSource, IBeneficiaryTableWizardDTO>(
        source,
        'BeneficiaryTableWizardSource',
        'BeneficiaryTableWizardDTO'
    );
};

// Inquiry Table Wizard
PojosMetadataMap.create<IInquiryTableWizardSource>('InquiryTableWizardSource', {
    formData: Object,
    step: Number
});

PojosMetadataMap.create<IInquiryTableWizardDTO>('InquiryTableWizardDTO', {
    productInformations: Object,
    endUser: Object,
    partiesInformation: Object,
    declarantInformation: Object,
    resellers: Array,
    priceInformation: Object
});

const convertToLegalEntityBriefDto = (legalEntity) => {
    if (!legalEntity?.id?.trim()) {
        return null;
    }
    return {
        address: legalEntity?.address,
        id: legalEntity?.id || '',
        name: legalEntity?.name || '',
        picture: legalEntity?.picture || '',
        presentation: legalEntity?.presentation
    };
};

const ensureReferentialKey = (referential: any): any => {
    if (!referential) {
        return referential;
    }
    return {
        ...referential,
        Key: referential.Key || referential.key
    };
};

const transformUnitStructure = (unit: any): any => {
    if (!unit) {
        return unit;
    }
    if (typeof unit === 'string') {
        const trimmed = unit.trim();
        if (!trimmed) {
            return undefined;
        }
        return {
            uniqueKey: trimmed,
            parentId: null
        };
    }
    return {
        key: unit.id || unit.key,
        uniqueKey: unit.Key || unit.uniqueKey,
        parentId: unit.parentId || null
    };
};

const resolveInquiryCurrencyUnit = (item: any, criteriaCurrency: any): any => {
    const transformed = [
        criteriaCurrency,
        item.customizedPrice?.unit,
        item.customizedPrice?.baseUnit,
        item.listPrice?.unit,
        item.listPrice?.baseUnit,
        item.unitPrice?.baseUnit,
        item.unitPrice?.unit
    ].map(transformUnitStructure);

    return transformed.find(t => t?.key) ?? transformed.find(t => t?.uniqueKey);
};

createMap<IInquiryTableWizardSource, IInquiryTableWizardDTO>(
    mapper,
    'InquiryTableWizardSource',
    'InquiryTableWizardDTO',
    forMember(
        d => d.productInformations,
        mapFrom(s => s.formData.productInformations.map(item => {
            const currencyUnit = resolveInquiryCurrencyUnit(item, s.formData.priceCalculationCriteria?.currency);
            const marginFields = item.cpsPercentage
                ?? (item as unknown as { margingRatePercentage?: typeof item.cpsPercentage }).margingRatePercentage;
            const percentUnitResolved = transformUnitStructure(marginFields?.unit || marginFields?.baseUnit);
            const toAmount = (num?: number | string) => {
                if (num === '' || num === null || num === undefined) {
                    return undefined;
                }
                const n = typeof num === 'string' ? Number(num.replace(',', '.')) : Number(num);
                return Number.isFinite(n) ? n : undefined;
            };
            const mapMoneyBlock = (price?: {
                baseNumber?: number | string | null;
                number?: string | null;
                baseUnit?: unknown;
                unit?: unknown;
            }) => {
                if (!price) return undefined;

                const { baseNumber: bnRaw, number } = price;

                const baseNumberOut =
                    bnRaw === null ? null
                    : typeof bnRaw === 'number' && Number.isFinite(bnRaw) ? bnRaw
                    : bnRaw != undefined && bnRaw !== '' ? toAmount(bnRaw as string)
                    : number?.trim() ? toAmount(number)
                    : toAmount(bnRaw ?? undefined);

                return {
                    baseNumber: baseNumberOut,
                    baseUnit: transformUnitStructure(price.baseUnit) || currencyUnit,
                    number: number ?? undefined,
                    unit: transformUnitStructure(price.unit) || currencyUnit
                };
            };
            return {
                productCode: ensureReferentialKey(item.productCode),
                model: item.model,
                units: toAmount(item?.units?.baseNumber ?? item?.units?.number),
                customizedPrice: mapMoneyBlock(item.customizedPrice),
                listPrice: mapMoneyBlock(item.listPrice),
                margingRatePercentage: marginFields || percentUnitResolved ? {
                    baseNumber: toAmount(marginFields?.baseNumber ?? marginFields?.number),
                    baseUnit: percentUnitResolved,
                    number: marginFields?.number,
                    unit: percentUnitResolved
                } : undefined,
                ...(item?.clusterStakeId ? { clusterStakeId: item.clusterStakeId } : {})
            } as unknown as IWizardProductInformationItem;
        }))
    ),
    forMember(
        d => d.endUser,
        mapFrom(s => {
            const sanitizeStakeProfile = (stakeProfile?: IWizardStakeProfile): IWizardStakeProfile | undefined => {
                if (!stakeProfile) {
                    return undefined;
                }
                const { groups, organizationsGroups, ...rest } = stakeProfile;
                // backend does not allow groups/organizationsGroups for end user payload
                return {
                    ...rest
                } as unknown as IWizardStakeProfile;
            };

            const { endUser, invoicedCustomer, ...rest } = s.formData.endUser;

            const mappedEndUser: IInquiryTableWizardDTO['endUser'] = {
                ...rest,
                endUser: sanitizeStakeProfile(endUser),
                invoicedCustomer: sanitizeStakeProfile(invoicedCustomer),
                endUserCategory: ensureReferentialKey(rest.endUserCategory),
                productDestination: ensureReferentialKey(rest.productDestination)
            };

            return mappedEndUser;
        })
    ),
    forMember(
        d => d.partiesInformation,
        mapFrom(s => {
            const pi = s.formData.partiesInformation;
            const localDistributor = wizardStakeProfileToInquiryPartiesWire(pi?.localDistributor);
            const clientCoordinator = wizardStakeProfileToInquiryPartiesWire(pi?.clientCoordinator);
            if (!localDistributor || !clientCoordinator) {
                return undefined;
            }
            return { localDistributor, clientCoordinator };
        })
    ),
    forMember(
        d => d.declarantInformation,
        mapFrom(s => {
            const { legalEntity, manager, ...rest } = s.formData.declarantInformation;
            const mappedLegalEntity = convertToLegalEntityBriefDto(legalEntity);
            const mappedManager = manager ? {
                ...manager,
                legalEntityBrief: (typeof manager.legalEntityBrief !== 'string' && convertToLegalEntityBriefDto(manager.legalEntityBrief)) || undefined
            } : undefined;

            return { ...rest, legalEntity: mappedLegalEntity, manager: mappedManager };
        })
    ),
    forMember(
        d => d.resellers,
        mapFrom(s => {
            if (!s.formData.resellers || s.formData.resellers.length === 0) {
                return undefined;
            }
            const filteredResellers: IWizardReseller[] = s.formData.resellers
                .filter(reseller => reseller.resellerName || reseller.country || reseller.quality)
                .map(reseller => ({
                    resellerName: convertToLegalEntityBriefDto(reseller.resellerName),
                    quality: ensureReferentialKey(reseller.quality),
                    country: ensureReferentialKey(reseller.country),
                    ...(reseller.clusterStakeId && { clusterStakeId: reseller.clusterStakeId })
                } as IWizardReseller));
            return filteredResellers.length > 0 ? filteredResellers : undefined;
        })
    )
);

export const mapInquiryTableWizard = (source: IInquiryTableWizardSource): IInquiryTableWizardDTO => {
    return mapper.map<IInquiryTableWizardSource, IInquiryTableWizardDTO>(
        source,
        'InquiryTableWizardSource',
        'InquiryTableWizardDTO'
    );
};

export { mapper };